import { useEffect, useState } from 'react'

/**
 * True once the page has painted and the browser has had a moment to itself
 * (requestIdleCallback, with a timeout so a busy page still gets there; a
 * plain timer where the API is missing). For what a page does not need to
 * show its content: the shell's notifications, chat and assistant used to
 * render, subscribe and fetch in the same tasks as the screen being opened.
 * Once true it stays true for the session, so a remount never waits again.
 */
let painted = false
const waiting = new Set<() => void>()
let scheduled = false

function schedule() {
  if (scheduled) return
  scheduled = true
  const done = () => {
    painted = true
    waiting.forEach((notify) => notify())
    waiting.clear()
  }
  // Two frames: the first paint has happened, then wait for idle.
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      if (typeof requestIdleCallback === 'function') requestIdleCallback(done, { timeout: 1500 })
      else window.setTimeout(done, 1200)
    }),
  )
}

export function useAfterFirstPaint(): boolean {
  const [ready, setReady] = useState(painted)
  useEffect(() => {
    if (painted) return
    const notify = () => setReady(true)
    waiting.add(notify)
    schedule()
    return () => {
      waiting.delete(notify)
    }
  }, [])
  return ready
}

/** Renders its children once the page has painted (see useAfterFirstPaint). */
export function AfterFirstPaint({ children }: { children: React.ReactNode }) {
  return useAfterFirstPaint() ? children : null
}
